import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Button } from 'ALIASED-antd';
import { css } from '@emotion/react';
import { useSessionStorage } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import useMediaPermissions, { MediaType } from '@hooks/useMediaPermissions';
import BlockedMediaPermissionsWarning from './BlockedMediaPermissionsWarning';

interface MediaPermissionsWarningProps {
  isInCallingWindow: boolean;
  storageKey?: string;
  style?: React.CSSProperties;
  callType?: 'AUDIO' | 'VIDEO';
}

const MediaPermissionsWarning = ({
  isInCallingWindow,
  storageKey,
  style,
  callType = 'VIDEO'
}: MediaPermissionsWarningProps) => {
  const { t } = useTranslation('common');
  const STORAGE_KEY = storageKey ? `media-permissions-banner-${storageKey}` : 'media-permissions-banner-default';

  const [permissions, setPermissions] = useState({
    camera: false,
    microphone: false,
    cameraBlocked: false,
    microphoneBlocked: false
  });

  const [isDismissed, setIsDismissed] = useSessionStorage(`${STORAGE_KEY}-dismissed`, false);
  const [wasBlockedInSession, setWasBlockedInSession] = useSessionStorage(`${STORAGE_KEY}-blocked`, false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isCheckingInitialPermissions, setIsCheckingInitialPermissions] = useState(true);
  const { requestPermissions, checkCurrentPermissions } = useMediaPermissions();

  const checkPermissionsStatus = useCallback(async () => {
    try {
      const result = await checkCurrentPermissions();

      setPermissions((prev) => {
        // Check if permissions are granted - if so, clear session blocked state
        if (result.video || result.audio) {
          setWasBlockedInSession(false);
        }

        // Keep blocked state from current session, previous component state, or API
        const shouldBeBlocked =
          wasBlockedInSession ||
          ((prev.cameraBlocked || prev.microphoneBlocked) && !result.video && !result.audio) ||
          result.videoBlocked === true ||
          result.audioBlocked === true;

        return {
          camera: result.video,
          microphone: result.audio,
          cameraBlocked: callType !== 'AUDIO' && shouldBeBlocked,
          microphoneBlocked: shouldBeBlocked
        };
      });
    } catch (err) {
      console.error('Failed to check permissions:', err);
    } finally {
      // Mark initial check as complete
      setIsCheckingInitialPermissions(false);
    }
  }, [checkCurrentPermissions, callType, wasBlockedInSession]);

  useEffect(() => {
    if (!isInCallingWindow) return;

    checkPermissionsStatus();

    const intervalId = setInterval(checkPermissionsStatus, 10000);

    return () => clearInterval(intervalId);
  }, [isInCallingWindow]);

  const handleGrantPermissions = async () => {
    setIsRequesting(true);
    try {
      const mediaType: MediaType = callType === 'AUDIO' ? 'audio' : 'both';
      const result = await requestPermissions(mediaType);

      // Update permissions state with successful grant
      setPermissions((prev) => ({
        ...prev,
        camera: result.video,
        microphone: result.audio
      }));
    } catch (err) {
      console.error('Failed to request permissions:', err);

      // Handle permission denial - immediately update blocked state
      if (err instanceof Error && err.name === 'NotAllowedError') {
        setPermissions((prev) => ({
          ...prev,
          // Mark permissions as blocked for immediate UI update
          cameraBlocked: callType !== 'AUDIO',
          microphoneBlocked: true
        }));

        // Store blocked state in sessionStorage to persist across route changes
        setWasBlockedInSession(true);
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  // Determine what permissions are needed and current state
  const hasRequiredPermissions =
    callType === 'AUDIO' ? permissions.microphone : permissions.camera && permissions.microphone;

  // Check if any required permissions are explicitly blocked
  const isBlocked = permissions.cameraBlocked || permissions.microphoneBlocked;

  // Don't show banner if permissions granted, not in calling window, dismissed, or still checking initial state
  if (hasRequiredPermissions || !isInCallingWindow || isDismissed || isCheckingInitialPermissions) {
    return null;
  }

  // Determine missing permissions for user-friendly message
  const missingPermissions = [];
  if (callType !== 'AUDIO' && !permissions.camera) missingPermissions.push(t('camera'));
  if (!permissions.microphone) missingPermissions.push(t('microphone'));

  if (isBlocked) {
    return <BlockedMediaPermissionsWarning {...{ style, handleDismiss, handleRefreshPage }} />;
  }

  return (
    <Alert
      css={css({ marginBottom: '1rem', ...style })}
      message={t('CallStartingSoon')}
      type="warning"
      showIcon
      closable
      afterClose={handleDismiss}
      description={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span>
            {t('MediaPermissionsNeededDescription', {
              permissions: missingPermissions.join(` ${t('and')} `)
            })}
          </span>
          <div>
            <Button
              type="primary"
              onClick={handleGrantPermissions}
              loading={isRequesting}
              data-testid="grant-permissions-button"
            >
              {t('GrantPermissions')}
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default MediaPermissionsWarning;
